import useWindowSize from "@/hooks/useWindowSize";
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  ChartData,
  ChartDataset,
  Chart as ChartJS,
  ChartOptions,
  DoughnutController,
  Legend,
  LegendItem,
  LineController,
  LineElement,
  LinearScale,
  PieController,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  BarController,
  LineController,
  DoughnutController,
  PieController,
  Title,
  Legend,
  Tooltip,
);

export type graphDatasetProps = {
  label: string;
  data: Array<number>;
};
export type mixGraphProps = {
  id: string;
  labels: Array<string>;
  datasets: {
    line: graphDatasetProps;
    bar1: graphDatasetProps;
    bar2: graphDatasetProps;
  };
};
export function MixGraph(props: mixGraphProps) {
  const data: ChartData = {
    labels: props.labels,
    datasets: [
      {
        type: "line" as const,
        label: props.datasets.line.label,
        borderColor: "rgb(120, 120, 120)",
        borderWidth: 2,
        fill: false,
        data: props.datasets.line.data,
      },
      {
        type: "bar" as const,
        label: props.datasets.bar1.label,
        backgroundColor: "rgb(53, 162, 235)",
        borderColor: "white",
        borderWidth: 2,
        data: props.datasets.bar1.data,
      },
      {
        type: "bar" as const,
        label: props.datasets.bar2.label,
        backgroundColor: "rgb(255, 99, 132)",
        borderColor: "white",
        borderWidth: 2,
        data: props.datasets.bar2.data,
      },
    ],
  };
  return <Chart type="bar" data={data} />;
}
export function DonutsGraph(props: ChartData) {
  // 数量合計値
  const totalValue = Number(
    props.datasets[0].data.reduce(function (previousValue, currentValue) {
      const sum: number = previousValue ? Number(previousValue) : 0;
      const val: number = currentValue ? Number(currentValue) : 0;
      return sum + val;
    }),
  );
  // 円中央のラベルをセット
  const fontSize = 14;
  const chartJsPluginCenterLabel = {
    id: "chartjs-plugin-center-label",
    afterDatasetsDraw: function (chart: ChartJS) {
      // console.dir(chart)
      // ラベルの X 座標と Y 座標
      const chartArea: {
        height: number;
        width: number;
        top: number;
        left: number;
      } = chart.chartArea;
      const labelY: number =
        chartArea.top - fontSize / 2 + Math.round(chartArea.height / 2);
      const labelX: number = chartArea.left + Math.round(chartArea.width / 2);
      // ラベル描画
      const ctx: CanvasRenderingContext2D = this.setTextStyle(chart.ctx);
      ctx.fillText(String(totalValue), labelX, labelY);
      ctx.fillText("合計", labelX, labelY + fontSize);
    },
    /** 書式設定 */
    setTextStyle: function (ctx: CanvasRenderingContext2D) {
      ctx.font = String(fontSize) + "px";
      ctx.fillStyle = "#636363";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      return ctx;
    },
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "left" as const,
      },
      title: {
        display: false,
        text: ["合計"],
        position: "bottom" as const,
      },
    },
  };
  const labelsWithPer: Array<string> = [];
  if (props.labels) {
    for (let index = 0; index < props.labels?.length; index++) {
      const label =
        String(props.labels[index]) +
        " " +
        String(
          Math.round(
            (Number(props.datasets[0].data[index]) / totalValue) * 10000,
          ) / 100,
        ) +
        "%";
      labelsWithPer.push(label);
    }
  }
  const graphData = {
    labels: labelsWithPer,
    datasets: props.datasets,
  };
  return (
    <Chart
      type="doughnut"
      options={options}
      data={graphData}
      plugins={[chartJsPluginCenterLabel]}
    />
  );
}
export function PieGraph(props: ChartData) {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
  };

  return <Chart type="pie" options={options} data={props} />;
}
export function BarGraph(props: ChartData) {
  const barGraphSet = {
    options: {
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
      },
    },
    data: {
      labels: props.labels,
      datasets: props.datasets,
    },
  };
  return (
    <Chart type="bar" options={barGraphSet.options} data={barGraphSet.data} />
  );
}

export function HorizontalBarGraph(props: {
  total: number;
  datasets: ChartDataset[];
  labels: string[];
}) {
  const fontSize = 16;
  const chartJsPluginCenterRightLabel = {
    id: "chartjs-plugin-center-right-label",
    afterDatasetsDraw: function (chart: ChartJS) {
      const ctx: CanvasRenderingContext2D = this.setTextStyle(chart.ctx);
      chart.data.datasets.forEach((dataset, idx) => {
        const meta = chart.getDatasetMeta(idx);
        if (!meta.hidden) {
          meta.data.forEach((element, metaIdx) => {
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const position = element.tooltipPosition();
            ctx.fillText(
              `${Number(dataset.data[metaIdx]) * props.total}人 / ${
                Number(dataset.data[metaIdx]) * 100
              }%`,
              position.x - 50,
              position.y,
            );
          });
        }
      });
    },
    setTextStyle: function (ctx: CanvasRenderingContext2D) {
      ctx.font = String(fontSize) + "px";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      return ctx;
    },
  };

  const barGraphSet: { options: ChartOptions; data: ChartData } = {
    options: {
      indexAxis: "y" as const,
      responsive: true,
      plugins: {
        legend: { display: false },
      },
      scales: {
        xAxis: {
          ticks: {
            callback: (value) => {
              return `${Number(value) * 100}%`;
            },
          },
          max: 1,
        },
      },
    },
    data: {
      labels: props.labels,
      datasets: props.datasets,
    },
  };
  return (
    <Chart
      type="bar"
      options={barGraphSet.options}
      data={barGraphSet.data}
      plugins={[chartJsPluginCenterRightLabel]}
    />
  );
}

export function LineGraph(props: ChartData) {
  const barGraphSet = {
    options: {
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
      },
    },
    data: {
      labels: props.labels,
      datasets: props.datasets,
    },
  };
  return (
    <Chart type="line" options={barGraphSet.options} data={barGraphSet.data} />
  );
}

interface BarGraphChartData extends ChartData {
  isSp?: boolean;
}

export function BarGraphChecklist(props: BarGraphChartData) {
  const barGraphSet = {
    options: {
      responsive: true,
      plugins: {
        legend: {
          onClick: () => false,
          position: "top" as const,
          align: "end" as const,
          labels: {
            color: "#000",
            font: { size: 10 },
            padding: 10,
            textAlign: "center" as const,
            usePointStyle: true,
          },
        },
        title: {
          display: false,
          text: ["直近6ヶ月の売上・顧客推移"],
          position: "top" as const,
          align: "start" as const,
          font: {
            size: 14,
            color: "#757575",
          },
        },
      },
      scales: {
        x: {
          display: true,
          ticks: {
            color: "#000",
            font: { size: 10 },
            padding: 8,
            maxRotation: 90,
            minRotation: 45,
          },
          grid: {
            color: "#FFF",
            borderColor: "#E5E7EB",
            drawTicks: false, // 軸領域の目盛りの横に線描画
          },
        },
        xTopPadding: {
          // Fake x-axis for padding
          display: true,
          position: "top",
          labels: [""],
          grid: {
            color: "#FFF",
            borderColor: "#E5E7EB",
            drawOnChartArea: false,
            drawTicks: false,
          },
        },
        yAxes: {
          display: true,
          title: {
            display: !props.isSp,
            align: "center",
            text: "顧客数累計",
            color: "#757575",
            font: { size: 10 },
          },
          ticks: {
            count: 4,
            stepSize: 1000,
            color: "#000",
            font: { size: 10 },
            callback: function (value: string | number) {
              return `${Math.round(Number(value))}` + "名";
            },
          },
          grid: {
            color: "#E5E7EB",
            borderColor: "#FFF",
            drawTicks: false, // 軸領域の目盛りの横に線描画
          },
          min: 0,
          suggestedMin: 0,
          suggestedMax: 10,
        },
        "right-y-axis": {
          display: true,
          position: "right" as const,
          title: {
            display: !props.isSp,
            text: "単月売上",
            color: "#757575",
            font: { size: 10 },
          },
          ticks: {
            count: 4,
            color: "#000",
            font: { size: 10 },
            callback: function (value: string | number) {
              return `${Math.round(Number(value)).toLocaleString()}` + "円";
            },
          },
          grid: {
            borderColor: "#FFF",
            display: false,
          },
          min: 0,
          suggestedMin: 0,
          suggestedMax: 300,
        },
      },
    },
    data: {
      labels: props.labels,
      datasets: props.datasets,
    },
    style: { maxHeight: "190px" },
  };
  return (
    <Chart
      type="bar"
      style={barGraphSet.style}
      options={barGraphSet.options}
      data={barGraphSet.data}
    />
  );
}

export function BarGraphService(props: ChartData) {
  const { isSp } = useWindowSize();

  const barGraphSet = {
    options: {
      responsive: true,
      plugins: {
        legend: {
          display: false,
          onClick: () => false,
          position: "top" as const,
          align: "end" as const,
          labels: {
            usePointStyle: true,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: "#222",
            font: {
              size: isSp ? 8 : 14,
            },
          },
          grid: {
            color: "#F7F8F9",
            borderColor: "#DDD",
            drawTicks: false,
          },
        },
        y: {
          ticks: {
            stepSize: 1,
          },
        },
      },
    },
    data: {
      labels: props.labels,
      datasets: props.datasets,
    },
  };
  return (
    <Chart type="bar" options={barGraphSet.options} data={barGraphSet.data} />
  );
}

export function PieGraphService(props: ChartData) {
  const maxSizeLabel = 10;
  const pieGraphSet = {
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "right" as const,
          align: "start" as const,
          labels: {
            boxWidth: 32,
            boxHeight: 32,
            filter: (legendItem: LegendItem) => {
              const [text, percent] = legendItem.text.split("\t");
              const textTruncate =
                text.length > maxSizeLabel
                  ? text.substring(0, maxSizeLabel) + "..." + percent
                  : text + percent;
              legendItem.borderRadius = 4;
              legendItem.text = textTruncate;
              return true;
            },
          },
        },
      },
    },
    data: {
      labels: props.labels,
      datasets: props.datasets,
    },
  };

  return (
    <Chart type="pie" options={pieGraphSet.options} data={pieGraphSet.data} />
  );
}
